import { ApolloError, ServerError } from "@apollo/client";
import { BackendErrorCode } from "core/types";
import Translations from "translations/types";

export type ApolloNetworkErrorResponse = {
  networkError?: ServerError;
};

const isError = (err: unknown): err is Error => {
  return err instanceof Error;
};

export const getError = (err: unknown): Error => {
  if (isError(err)) {
    return err;
  } else {
    return new Error("Generic error");
  }
};

const isApolloError = (err: unknown): err is ApolloError => {
  return err instanceof ApolloError;
};

export const getApolloError = (err: unknown): ApolloError => {
  if (isApolloError(err)) {
    return err;
  } else {
    return new ApolloError({ errorMessage: "Generic error" });
  }
};

export const getApolloNetworkErrorMessage = (err: unknown) => {
  const error = getApolloError(err) as ApolloNetworkErrorResponse;

  const errorMessage =
    typeof error?.networkError?.result === "object"
      ? error.networkError.result.error
      : error?.networkError?.result;

  return errorMessage || JSON.stringify(err);
};

export const getErrorMessage = (err: unknown): string => {
  return getError(err).message;
};

export type ApolloNetworkErrorDetails = {
  result?: ServerError["result"];
  statusCode?: number;
};

export function getApolloNetworkErrorDetails(
  err: unknown,
): ApolloNetworkErrorDetails | undefined {
  if (typeof err !== "object" || err === null) {
    return undefined;
  }

  const networkError = (err as ApolloNetworkErrorResponse).networkError;
  if (!networkError) {
    return undefined;
  }

  return {
    result: networkError.result,
    statusCode: networkError.statusCode,
  };
}

export function getBackendNetworkErrorMessage(
  err: unknown,
): string | undefined {
  const { result } = getApolloNetworkErrorDetails(err) ?? {};

  if (typeof result === "object" && result !== null && "error" in result) {
    const error = (result as { error?: unknown }).error;
    return typeof error === "string" ? error : undefined;
  }

  if (typeof result === "string") {
    return result;
  }

  return undefined;
}

function getStatusFromTransportMessage(message: string): number | undefined {
  try {
    const json = JSON.parse(message) as { status?: unknown };
    return typeof json.status === "number" ? json.status : undefined;
  } catch {
    return undefined;
  }
}

export function tryGetErrorStatus(err: unknown): number | undefined {
  if (typeof err === "object" && err !== null) {
    if ("status" in err && typeof err.status === "number") {
      return err.status;
    }

    const statusCode = getApolloNetworkErrorDetails(err)?.statusCode;
    if (typeof statusCode === "number") {
      return statusCode;
    }
  }

  const transportStatus = getStatusFromTransportMessage(getError(err).message);
  if (transportStatus != null) {
    return transportStatus;
  }

  return undefined;
}

export function getErrorStatus(err: unknown, fallback: number): number {
  return tryGetErrorStatus(err) ?? fallback;
}

/** Seald SDK message when the identity was revoked in Seald (often does not contain the word "seald"). */
export const isSealdIdentityRevokedErrorMessage = (
  message: string,
): boolean => {
  const lower = message.toLowerCase();
  return (
    lower.includes("user or identity has been revoked") ||
    lower.includes("identity has been revoked")
  );
};

/**
 * This type guard checks if a given error code exists in our translation system.
 * For the full list of error codes and their meanings, see:
 * https://www.notion.so/recare/Custom-Error-Codes-Mapping-To-Translations-198a736bb0a880c09a8afa5b78aba2c8
 */
export const isBackendErrorCode = (
  value: unknown,
  translations: Translations,
): value is BackendErrorCode => {
  const key = String(value);
  return Object.keys(translations.general.errorCodes).includes(key);
};
